/**
 * SEO Head Component
 * 
 * Manages all SEO-related meta tags, Open Graph tags, Twitter Cards,
 * and structured data for each page using react-helmet-async.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import type { SEOConfig } from '../../config/seo';
import { generateTitle, generateCanonical, generateOgImage, SITE_CONFIG, getRouteSEO, mergeSEO } from '../../config/seo';

export interface SeoHeadProps extends Partial<SEOConfig> {
  /**
   * Page title (will be merged with site name)
   */
  title?: string;
  
  /**
   * Page description
   */
  description?: string;
  
  /**
   * SEO keywords array
   */
  keywords?: string[];
  
  /**
   * Open Graph image path or URL
   */
  ogImage?: string;
  
  /**
   * Open Graph type
   */
  ogType?: 'website' | 'article' | 'product' | 'profile';
  
  /**
   * Canonical URL (auto-generated from path if not provided)
   */
  canonical?: string;
  
  /**
   * Noindex flag (prevents search engine indexing)
   */
  noindex?: boolean;
  
  /**
   * Nofollow flag (prevents search engines from following links)
   */
  nofollow?: boolean;
  
  /**
   * Additional structured data to include
   */
  structuredData?: React.ReactNode;
  
  /**
   * Custom meta tags
   */
  customMeta?: Array<{ name?: string; property?: string; content: string }>;
}

/**
 * SeoHead Component
 * 
 * Manages page-level SEO meta tags, Open Graph, Twitter Cards,
 * and canonical URLs. Uses route-based defaults when available.
 */
export default function SeoHead({
  title,
  description,
  keywords,
  ogImage,
  ogType,
  canonical,
  noindex = false,
  nofollow = false,
  structuredData,
  customMeta = [],
}: SeoHeadProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Get route-specific SEO if available
  const routeSEO = getRouteSEO(currentPath);
  const seo = mergeSEO(routeSEO, {
    title,
    description,
    keywords,
    ogImage,
    ogType,
    canonical: canonical || generateCanonical(currentPath),
  });

  const finalTitle = generateTitle(seo.title);
  const finalDescription = seo.description || SITE_CONFIG.defaultDescription;
  const finalKeywords = seo.keywords?.join(', ') || '';
  const finalOgImage = generateOgImage(seo.ogImage);
  const finalCanonical = seo.canonical || generateCanonical(currentPath);
  const finalOgType = seo.ogType || 'website';

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{finalTitle}</title>
        <meta name="title" content={finalTitle} />
        <meta name="description" content={finalDescription} />
        {finalKeywords && <meta name="keywords" content={finalKeywords} />}
        <link rel="canonical" href={finalCanonical} />

        {/* Robots */}
        {noindex && <meta name="robots" content="noindex" />}
        {nofollow && <meta name="robots" content="nofollow" />}
        {noindex && nofollow && <meta name="robots" content="noindex, nofollow" />}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content={finalOgType} />
        <meta property="og:url" content={finalCanonical} />
        <meta property="og:title" content={finalTitle} />
        <meta property="og:description" content={finalDescription} />
        <meta property="og:image" content={finalOgImage} />
        <meta property="og:site_name" content={SITE_CONFIG.name} />
        <meta property="og:locale" content={SITE_CONFIG.locale} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={finalCanonical} />
        <meta name="twitter:title" content={finalTitle} />
        <meta name="twitter:description" content={finalDescription} />
        <meta name="twitter:image" content={finalOgImage} />
        {SITE_CONFIG.twitterHandle && (
          <meta name="twitter:site" content={SITE_CONFIG.twitterHandle} />
        )}

        {/* Additional Custom Meta Tags */}
        {customMeta.map((meta, index) => (
          <meta
            key={index}
            name={meta.name}
            property={meta.property}
            content={meta.content}
          />
        ))}
      </Helmet>
      {/* Structured Data - rendered outside Helmet to avoid nesting issues with React 19 */}
      {/* JSON-LD script tags work fine in the body - search engines will read them */}
      {structuredData}
    </>
  );
}